import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import { Box, Heading, Stack } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { borders, boxShadows, colors, spaces } from '@99designs/emu/settings';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { EstimatedPrice } from '@99designs/graph-utils/types';
import PulsingBox from '../../lib/components/PulsingBox';

const ProductImage = styled.img`
    width: 100%;
    height: 100%;
    border-radius: ${borders.borderRadiuses.large}px;
`;

const RecommendationsBoxStyles = css`
    color: ${colors.grey800};
    flex: 0 0 190px;
    min-width: 190px;
    margin-right: ${spaces['1.5bu']};

    ${mediaQuery.maximum('medium')} {
        flex: 1 0 120px;
        min-width: 120px;
        margin-right: ${spaces['1bu']};
    }

    :last-child {
        margin-right: 0px;
    }
`;

const RecommendationsBox = styled.a`
    ${RecommendationsBoxStyles}

    :hover,
    :focus {
        color: ${colors.grey800};
        text-decoration: none;
    }

    :hover {
        ${mediaQuery.minimum('normal')} {
            ${ProductImage} {
                box-shadow: ${boxShadows.high};
            }
        }
    }

    :focus {
        border: 1px dashed ${colors.grey700};
        outline: none;
    }
`;

interface PrintRecommendationItemProps {
    title: string;
    url: string;
    image: string;
    onClick: () => void;
    altText: string;
    estimatedPrice?: EstimatedPrice;
}

function PrintRecommendationItem({
    title,
    url,
    image,
    altText,
    onClick,
    estimatedPrice,
}: PrintRecommendationItemProps) {
    const intl = useIntl();

    return (
        <RecommendationsBox href={url} onClick={onClick} target="_blank" rel="noreferrer">
            <Stack space="0.5bu">
                <ProductImage src={image} alt={altText} />
                <Box>
                    <Heading accessibilityLevel={5}>{title}</Heading>
                    {estimatedPrice && (
                        <Box>
                            {formatMessage(__('From {price}'), {
                                price: intl.formatNumber(estimatedPrice.price, {
                                    style: 'currency',
                                    currency: estimatedPrice.currency,
                                    minimumFractionDigits: 2,
                                }),
                            })}
                        </Box>
                    )}
                </Box>
            </Stack>
        </RecommendationsBox>
    );
}

const RecommendationsBoxSkeleton = styled.div`
    ${RecommendationsBoxStyles}
`;

const SkeletonImage = styled(PulsingBox)`
    border-radius: ${borders.borderRadiuses.large}px;
    padding-bottom: 100%;
`;

export function PrintRecommendationItemSkeleton() {
    return (
        <RecommendationsBoxSkeleton data-testid="recommendations-skeleton">
            <Stack space="0.75bu">
                <SkeletonImage />
                <PulsingBox height="25px" />
            </Stack>
        </RecommendationsBoxSkeleton>
    );
}

export default PrintRecommendationItem;
